"use client";

/**
 * ScoreTranscriber — "back to the roots": turn any recording on BB into MIDI
 * and engraved sheet music, entirely in the browser at zero server cost.
 *
 * Pipeline (all client-side, all open-source):
 *   audio → WebAudio decode → resample 22.05kHz mono
 *         → Basic Pitch (Spotify's open TF.js model, served from /public)
 *         → note events → MIDI file (@tonejs/midi)
 *         → quantized ABC notation → Verovio (WASM engraver) → SVG score
 *
 * Includes transpose (±12 st) and snap-to-scale (major/minor, any root) that
 * re-shape both the MIDI download and the engraved score — Gordon: "change
 * the key and scale of any analysed track".
 *
 * This is Phase 1 of the RipX/Melodyne-style analysis roadmap (see CLAUDE.md
 * backlog): stems (Demucs), multi-instrument transcription (MuScriptor),
 * word-aligned lyrics and MusicXML export come later.
 */

import { useCallback, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Music4, Download, FileMusic } from "lucide-react";
import { cn } from "@/lib/utils";

interface NoteEventTime {
  startTimeSeconds: number;
  durationSeconds: number;
  pitchMidi: number;
  amplitude: number;
}

interface Props {
  audioUrl: string;
  title: string;
  artist?: string;
  bpm?: number | null;
  keyName?: string | null;
  className?: string;
}

/** Longest stretch of audio we analyse in one go — keeps the in-browser
 *  model's memory use sane on long DJ mixes. */
const MAX_ANALYSIS_SECONDS = 240;

const MAJOR_SCALE = [0, 2, 4, 5, 7, 9, 11];
const MINOR_SCALE = [0, 2, 3, 5, 7, 8, 10];
const ROOT_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

/** Move a pitch to the nearest tone of the given scale (tie breaks downward). */
function snapPitchToScale(pitch: number, rootPc: number, scale: number[]): number {
  const rel = (((pitch - rootPc) % 12) + 12) % 12;
  if (scale.includes(rel)) return pitch;
  for (let d = 1; d <= 6; d++) {
    if (scale.includes((((rel - d) % 12) + 12) % 12)) return pitch - d;
    if (scale.includes((rel + d) % 12)) return pitch + d;
  }
  return pitch;
}

/** ABC pitch with an EXPLICIT accidental on every note (^ sharp / = natural),
 *  so bar-carryover rules can never mis-render the preview. Middle C (60) = C. */
function midiToAbc(pitch: number): string {
  const ACC = ["=", "^", "=", "^", "=", "=", "^", "=", "^", "=", "^", "="];
  const LTR = ["C", "C", "D", "D", "E", "F", "F", "G", "G", "A", "A", "B"];
  const pc = ((pitch % 12) + 12) % 12;
  const oct = Math.floor(pitch / 12) - 1;
  const acc = ACC[pc];
  const letter = LTR[pc];
  if (oct >= 5) return acc + letter.toLowerCase() + "'".repeat(oct - 5);
  return acc + letter + ",".repeat(Math.max(0, 4 - oct));
}

/** Quantize note events to a 16th grid and write monophonic-ish ABC (chords for
 *  shared onsets, rests for gaps, bars every 16 sixteenths, 64-bar preview cap).
 *  The MIDI download keeps full un-quantized fidelity — this is the readable view. */
function notesToAbc(notes: NoteEventTime[], bpm: number, title: string, artist: string | undefined, mapPitch: (p: number) => number): string {
  const sixteenth = 60 / bpm / 4;
  const byStart = new Map<number, { pitches: Set<number>; dur: number }>();
  for (const n of notes) {
    const qs = Math.round(n.startTimeSeconds / sixteenth);
    const qd = Math.min(32, Math.max(1, Math.round(n.durationSeconds / sixteenth)));
    const g = byStart.get(qs);
    if (g) { g.pitches.add(mapPitch(n.pitchMidi)); g.dur = Math.min(g.dur, qd); }
    else byStart.set(qs, { pitches: new Set([mapPitch(n.pitchMidi)]), dur: qd });
  }
  const starts = [...byStart.keys()].sort((a, b) => a - b);
  const MAX_UNITS = 64 * 16;
  let cursor = 0;
  const tokens: string[] = [];
  const emit = (token: (len: number) => string, length: number) => {
    let left = length;
    while (left > 0 && cursor < MAX_UNITS) {
      const space = 16 - (cursor % 16);
      const take = Math.min(left, space);
      tokens.push(token(take));
      cursor += take;
      left -= take;
      if (cursor % 16 === 0) tokens.push((cursor / 16) % 4 === 0 ? "|\n" : "|");
    }
  };
  for (let i = 0; i < starts.length && cursor < MAX_UNITS; i++) {
    const qs = starts[i];
    if (qs > cursor) emit(len => `z${len}`, qs - cursor);
    if (cursor >= MAX_UNITS) break;
    const g = byStart.get(qs)!;
    const next = starts[i + 1];
    const dur = Math.max(1, Math.min(g.dur, next !== undefined ? next - qs : g.dur));
    const ps = [...g.pitches].sort((a, b) => a - b).map(midiToAbc);
    const chord = ps.length === 1 ? ps[0] : `[${ps.join("")}]`;
    emit(len => `${chord}${len}`, dur);
  }
  const body = tokens.join(" ").replace(/\|\n /g, "|\n") + " |]";
  return [
    "X:1",
    `T:${title.replace(/[\r\n]/g, " ")}`,
    ...(artist ? [`C:${artist.replace(/[\r\n]/g, " ")}`] : []),
    "M:4/4",
    "L:1/16",
    `Q:1/4=${Math.round(bpm)}`,
    "K:C",
    body,
  ].join("\n");
}

/** Load the Verovio WASM engraver once, via its official CDN script. */
let verovioPromise: Promise<any> | null = null;
function loadVerovio(): Promise<any> {
  if (verovioPromise) return verovioPromise;
  verovioPromise = new Promise((resolve, reject) => {
    const w = window as any;
    const make = () => { try { resolve(new w.verovio.toolkit()); } catch (e) { reject(e); } };
    if (w.verovio?.toolkit) return make();
    const s = document.createElement("script");
    s.src = "https://www.verovio.org/javascript/latest/verovio-toolkit-wasm.js";
    s.onload = () => {
      const v = w.verovio;
      if (!v?.module) return reject(new Error("verovio missing"));
      if (v.module.calledRun || v.module.runtimeInitialized) make();
      else v.module.onRuntimeInitialized = make;
    };
    s.onerror = () => reject(new Error("verovio load failed"));
    document.head.appendChild(s);
  }).catch(err => { verovioPromise = null; throw err; });
  return verovioPromise;
}

type Status = "idle" | "decoding" | "transcribing" | "engraving" | "done" | "error";

export function ScoreTranscriber({ audioUrl, title, artist, bpm, keyName, className }: Props) {
  const t = useTranslations("music.score");
  const [status, setStatus] = useState<Status>("idle");
  const [pct, setPct] = useState(0);
  const [capped, setCapped] = useState(false);
  const [svg, setSvg] = useState("");
  const [engraveError, setEngraveError] = useState(false);
  const [transpose, setTranspose] = useState(0);
  const [snapMode, setSnapMode] = useState<"off" | "major" | "minor">("off");
  const [snapRoot, setSnapRoot] = useState(0);
  const notesRef = useRef<NoteEventTime[]>([]);

  const effectiveBpm = bpm && bpm > 30 ? bpm : 120;

  const mapPitch = useCallback((p: number) => {
    let out = p + transpose;
    if (snapMode !== "off") out = snapPitchToScale(out, snapRoot, snapMode === "major" ? MAJOR_SCALE : MINOR_SCALE);
    return Math.max(0, Math.min(127, out));
  }, [transpose, snapMode, snapRoot]);

  const engrave = useCallback(async (notes: NoteEventTime[], map: (p: number) => number) => {
    setStatus("engraving");
    setEngraveError(false);
    try {
      const abc = notesToAbc(notes, effectiveBpm, title, artist, map);
      const tk = await loadVerovio();
      tk.setOptions({ from: "abc", pageWidth: 1600, scale: 38, adjustPageHeight: true, header: "none", footer: "none" });
      const ok = tk.loadData(abc);
      if (!ok) throw new Error("loadData failed");
      setSvg(tk.renderToSVG(1, {}));
    } catch (e) {
      console.error("[score] engrave failed:", e);
      setSvg("");
      setEngraveError(true);
    }
    setStatus("done");
  }, [effectiveBpm, title, artist]);

  const transcribe = useCallback(async () => {
    setStatus("decoding");
    setPct(0);
    setCapped(false);
    try {
      const { decodeAudioFromUrl } = await import("@/lib/audio-analysis");
      const decoded = await decodeAudioFromUrl(audioUrl);
      const seconds = Math.min(decoded.duration, MAX_ANALYSIS_SECONDS);
      if (decoded.duration > MAX_ANALYSIS_SECONDS) setCapped(true);
      const sr = 22050;
      const oac = new OfflineAudioContext(1, Math.ceil(seconds * sr), sr);
      const src = oac.createBufferSource();
      src.buffer = decoded;
      src.connect(oac.destination);
      src.start();
      const resampled = await oac.startRendering();

      setStatus("transcribing");
      const bp = await import("@spotify/basic-pitch");
      const model = new bp.BasicPitch("/models/basic-pitch/model.json");
      const frames: number[][] = [], onsets: number[][] = [], contours: number[][] = [];
      await model.evaluateModel(
        resampled,
        (f, o, c) => { frames.push(...f); onsets.push(...o); contours.push(...c); },
        (p) => setPct(Math.round(p * 100)),
      );
      const notes = bp.noteFramesToTime(
        bp.addPitchBendsToNoteEvents(contours, bp.outputToNotesPoly(frames, onsets, 0.3, 0.3, 5)),
      ).sort((a, b) => a.startTimeSeconds - b.startTimeSeconds);
      if (notes.length === 0) throw new Error("no notes");
      notesRef.current = notes;
      await engrave(notes, mapPitch);
    } catch (e) {
      console.error("[score] transcription failed:", e);
      setStatus("error");
    }
  }, [audioUrl, engrave, mapPitch]);

  // Re-engrave when transpose/scale changes after a completed run.
  const applyPitchChange = useCallback((next: { transpose?: number; snapMode?: "off" | "major" | "minor"; snapRoot?: number }) => {
    const tr = next.transpose ?? transpose;
    const sm = next.snapMode ?? snapMode;
    const sc = next.snapRoot ?? snapRoot;
    if (next.transpose !== undefined) setTranspose(next.transpose);
    if (next.snapMode !== undefined) setSnapMode(next.snapMode);
    if (next.snapRoot !== undefined) setSnapRoot(next.snapRoot);
    if (notesRef.current.length > 0) {
      const map = (p: number) => {
        let out = p + tr;
        if (sm !== "off") out = snapPitchToScale(out, sc, sm === "major" ? MAJOR_SCALE : MINOR_SCALE);
        return Math.max(0, Math.min(127, out));
      };
      engrave(notesRef.current, map);
    }
  }, [transpose, snapMode, snapRoot, engrave]);

  const safeName = (ext: string) => `${(title || "track").replace(/[^\w\s-]/g, "").trim() || "track"}.${ext}`;

  const downloadMidi = useCallback(async () => {
    const { Midi } = await import("@tonejs/midi");
    const midi = new Midi();
    midi.header.setTempo(effectiveBpm);
    const track = midi.addTrack();
    track.name = title;
    for (const n of notesRef.current) {
      track.addNote({
        midi: mapPitch(n.pitchMidi),
        time: n.startTimeSeconds,
        duration: Math.max(0.05, n.durationSeconds),
        velocity: Math.min(1, Math.max(0.1, n.amplitude)),
      });
    }
    const blob = new Blob([midi.toArray()], { type: "audio/midi" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = safeName("mid");
    a.click();
    URL.revokeObjectURL(a.href);
  }, [effectiveBpm, title, mapPitch]);

  const downloadAbc = useCallback(() => {
    const abc = notesToAbc(notesRef.current, effectiveBpm, title, artist, mapPitch);
    const blob = new Blob([abc], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = safeName("abc");
    a.click();
    URL.revokeObjectURL(a.href);
  }, [effectiveBpm, title, artist, mapPitch]);

  const busy = status === "decoding" || status === "transcribing" || status === "engraving";

  if (!audioUrl) return null;

  return (
    <div className={cn("rounded-xl border border-border/60 bg-secondary/10 p-4 space-y-3", className)}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Music4 className="h-4 w-4 text-primary" />
          {t("title")}
          <Badge variant="outline" className="text-[10px] uppercase">{t("beta")}</Badge>
        </h3>
        <Button size="sm" onClick={transcribe} disabled={busy} className="gap-1.5">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileMusic className="h-3.5 w-3.5" />}
          {status === "decoding" ? t("decoding")
            : status === "transcribing" ? `${t("transcribing")} ${pct}%`
            : status === "engraving" ? t("engraving")
            : status === "done" ? t("redetect") : t("detect")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{t("intro")}</p>

      {status === "error" && <p className="text-xs text-destructive">{t("failed")}</p>}

      {status === "done" && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
            <span>{t("notesFound", { count: notesRef.current.length })}</span>
            {keyName && <span>· {keyName}</span>}
            <span>· {Math.round(effectiveBpm)} BPM</span>
            {capped && <span>· {t("cappedNote", { minutes: MAX_ANALYSIS_SECONDS / 60 })}</span>}
          </div>

          {/* Key & scale controls — reshape MIDI + score */}
          <div className="flex items-end gap-2 flex-wrap">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase text-muted-foreground">{t("transpose")}</Label>
              <Select value={String(transpose)} onValueChange={v => applyPitchChange({ transpose: Number(v) })}>
                <SelectTrigger className="h-8 w-[90px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 25 }, (_, i) => i - 12).map(s => (
                    <SelectItem key={s} value={String(s)}>{s > 0 ? `+${s}` : s} st</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase text-muted-foreground">{t("snapToScale")}</Label>
              <Select value={snapMode} onValueChange={v => applyPitchChange({ snapMode: v as any })}>
                <SelectTrigger className="h-8 w-[110px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">{t("off")}</SelectItem>
                  <SelectItem value="major">{t("major")}</SelectItem>
                  <SelectItem value="minor">{t("minor")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {snapMode !== "off" && (
              <Select value={String(snapRoot)} onValueChange={v => applyPitchChange({ snapRoot: Number(v) })}>
                <SelectTrigger className="h-8 w-[70px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROOT_NAMES.map((n, i) => <SelectItem key={n} value={String(i)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={downloadMidi}>
                <Download className="h-3.5 w-3.5" />{t("downloadMidi")}
              </Button>
              <Button size="sm" variant="ghost" className="h-8 gap-1.5 text-xs" onClick={downloadAbc}>
                <Download className="h-3.5 w-3.5" />{t("downloadAbc")}
              </Button>
            </div>
          </div>

          {/* Engraved score — Verovio SVG; engraving ink is black, keep white bg in all themes */}
          {svg ? (
            <div className="rounded-lg bg-white p-3 overflow-x-auto border border-border/40" dangerouslySetInnerHTML={{ __html: svg }} />
          ) : engraveError ? (
            <p className="text-xs text-amber-500">{t("engraveFailed")}</p>
          ) : null}

          <p className="text-[10px] text-muted-foreground/70">{t("local")}</p>
        </div>
      )}
    </div>
  );
}
